import { ApolloError } from "@apollo/client";
import { Alert, Box, Button, Card, CardContent, Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuthProvider, useCheckAuth, useDataProvider, useNotify, useRedirect, useTranslate } from "react-admin";

import {
  CompanySignInOutcome,
  completeAdminMfa,
  completeCompanySignInFromUrl,
  finishAdminMfaLogin,
  hasCompanySignInCallback,
  requestLearningPasswordReset,
  resetLearningPassword,
  startCompanySignIn,
} from "../auth/authProvider";
import {
  COMPANY_SIGN_IN_REQUIRED_ERROR,
  COMPANY_SIGN_IN_UNAVAILABLE_ERROR,
  requiresCompanySignIn,
} from "../auth/companySignIn";
import {
  AdminMfaResponse,
  AdminMfaStatus,
  LoginStatus,
  ManagerRegistrationCandidate,
  UserType,
} from "../generated/graphql";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { getAuthData, storeAuthData } from "../lib/authStorage";
import { color, space } from "../theme/designTokens";
import { RegistrationCandidate } from "../types/registration";
import { AdminMfaForm } from "./AdminMfaForm";
import { ForgotPasswordForm, ForgotPasswordFormData } from "./ForgotPasswordForm";
import { Logo } from "./icons/logo";
import { LoginForm, LoginFormData } from "./LoginForm";
import { RegistrationForm, RegistrationFormData } from "./RegistrationForm";
import { ResetPasswordForm, ResetPasswordFormData } from "./ResetPasswordForm";

// ViewMode enum instead of routing: auth flow shares state (email, validation code) across steps.
// Routing would require passing state via location/URL and handling refresh/bookmark edge cases.
enum ViewMode {
  Login = "login",
  Registration = "registration",
  ForgotPassword = "forgotPassword",
  ResetPassword = "resetPassword",
  AdminMfa = "adminMfa",
  AdminMfaRecoveryCodes = "adminMfaRecoveryCodes",
}

const COMPANY_SIGN_IN_MESSAGES = {
  [CompanySignInOutcome.Failed]: "login.companySignInFailed",
  [CompanySignInOutcome.NoAccess]: "login.companySignInNoAccess",
  [CompanySignInOutcome.Cancelled]: "login.companySignInCancelled",
} as const;

type LoginNotice = { severity: "error" | "warning"; message: string };

const hasErrorCode = (error: unknown, code: string): boolean =>
  (error instanceof ApolloError && error.graphQLErrors.some(item => item.message === code)) ||
  (error instanceof Error && error.message === code);

type LoginState = {
  loading: boolean;
  viewMode: ViewMode;
  activeEmail: string; // Email for current auth operation (registration or password reset)
  pendingValidationCode: string;
  registrationCandidate: RegistrationCandidate | null;
  mfaResponse: AdminMfaResponse | null;
  mfaError: string | null;
  mfaIsReplacement: boolean;
  mfaAccessToken: string | null;
  mfaRecoveryCodes: string[];
  mfaResetCodeVersion: number;
  loginNotice: LoginNotice | null;
};

export const CustomLogin: React.FC = () => {
  const authProvider = useAuthProvider();
  const dataProvider = useDataProvider();
  const checkAuth = useCheckAuth();
  const redirect = useRedirect();
  const translate = useTranslate();
  const handleError = useErrorHandler();
  const notify = useNotify();

  const [state, setState] = useState<LoginState>({
    // The form stays disabled while the return from the company sign-in is completed.
    loading: hasCompanySignInCallback(),
    viewMode: ViewMode.Login,
    activeEmail: "",
    pendingValidationCode: "",
    registrationCandidate: null,
    mfaResponse: null,
    mfaError: null,
    mfaIsReplacement: false,
    mfaAccessToken: null,
    mfaRecoveryCodes: [],
    mfaResetCodeVersion: 0,
    loginNotice: null,
  });

  // Check if user is already authenticated and redirect them away from login
  useEffect(() => {
    checkAuth({}, false)
      .then(() => {
        redirect("/");
      })
      .catch(() => {
        console.info("User is not authenticated");
      });
  }, [checkAuth, redirect]);

  // No "active" guard: React StrictMode runs effects twice and the first run owns the URL.
  useEffect(() => {
    const finishCompanySignIn = async () => {
      let outcome: CompanySignInOutcome;
      try {
        outcome = await completeCompanySignInFromUrl();
      } catch {
        outcome = CompanySignInOutcome.Failed;
      }
      if (outcome === CompanySignInOutcome.None) return;
      if (outcome === CompanySignInOutcome.Authenticated) {
        redirect("/");
        return;
      }
      const loginNotice: LoginNotice = {
        severity: outcome === CompanySignInOutcome.Cancelled ? "warning" : "error",
        message: translate(COMPANY_SIGN_IN_MESSAGES[outcome]),
      };
      setState(prev => ({ ...prev, loading: false, loginNotice }));
    };
    void finishCompanySignIn();
  }, [redirect, translate]);

  // Leaving for the provider can park this page in the back/forward cache with the form still loading.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setState(prev => ({ ...prev, loading: false }));
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const clearMfaState = (viewMode = ViewMode.Login) => {
    setState(prev => ({
      ...prev,
      loading: false,
      viewMode,
      mfaResponse: null,
      mfaError: null,
      mfaIsReplacement: false,
      mfaAccessToken: null,
      mfaRecoveryCodes: [],
      pendingValidationCode: "",
      registrationCandidate: null,
      mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
    }));
  };

  const enterAdminMfa = (mfaResponse: AdminMfaResponse, email?: string) => {
    if (mfaResponse.status === AdminMfaStatus.Expired || mfaResponse.status === AdminMfaStatus.Locked) {
      const lockedMessage =
        mfaResponse.retryAfterSeconds === undefined || mfaResponse.retryAfterSeconds === null
          ? translate("mfa.locked")
          : translate("mfa.lockedWithRetry", { seconds: mfaResponse.retryAfterSeconds });
      setState(prev => ({
        ...prev,
        loading: false,
        viewMode: ViewMode.AdminMfa,
        ...(email ? { activeEmail: email } : {}),
        pendingValidationCode: "",
        mfaResponse: null,
        mfaError: mfaResponse.status === AdminMfaStatus.Locked ? lockedMessage : translate("mfa.expired"),
        mfaIsReplacement: false,
        mfaAccessToken: null,
        mfaRecoveryCodes: [],
        mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      loading: false,
      viewMode: ViewMode.AdminMfa,
      ...(email ? { activeEmail: email } : {}),
      pendingValidationCode: "",
      registrationCandidate: null,
      mfaResponse,
      mfaError: null,
      mfaIsReplacement: false,
      mfaAccessToken: null,
      mfaRecoveryCodes: [],
      mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
    }));
  };

  const handleLoginResult = async (result: unknown, email: string, password: string): Promise<void> => {
    if (!result || typeof result !== "object") throw new Error("Login failed");
    const loginResult = result as {
      jwtToken?: string | null;
      loginStatus?: LoginStatus;
      mfa?: AdminMfaResponse | null;
      surface?: "admin" | "learning";
    };

    if (loginResult.mfa) {
      enterAdminMfa(loginResult.mfa, email);
      return;
    }

    if (loginResult.loginStatus === LoginStatus.Registering) {
      const staffCandidate = await dataProvider.getStaffRegistrationCandidate({ email, code: password });
      let registrationCandidate: RegistrationCandidate;
      if (staffCandidate) {
        registrationCandidate = { kind: UserType.CustomerService, roles: staffCandidate.roles };
      } else {
        let managerCandidate: ManagerRegistrationCandidate;
        try {
          managerCandidate = await dataProvider.getManagerRegistrationCandidate({ email, code: password });
        } catch (error) {
          if (!hasErrorCode(error, "ambiguous-customer-email")) throw error;
          notify(translate("crm.accountManagers.dialog.ambiguousEmail"), { type: "error" });
          setState(prev => ({ ...prev, loading: false }));
          return;
        }
        registrationCandidate = {
          kind: UserType.Manager,
          managerName: managerCandidate.managerName,
          organizationName: managerCandidate.organizationName,
        };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        viewMode: ViewMode.Registration,
        activeEmail: email,
        pendingValidationCode: password,
        registrationCandidate,
      }));
      return;
    }

    if (loginResult.loginStatus === LoginStatus.ResettingPassword) {
      setState(prev => ({
        ...prev,
        loading: false,
        viewMode: ViewMode.ResetPassword,
        activeEmail: email,
        pendingValidationCode: password,
      }));
      return;
    }

    if (loginResult.jwtToken) {
      redirect(loginResult.surface === "learning" ? "/learning" : "/");
      return;
    }

    throw new Error("Login failed");
  };

  const handleCompanySignIn = async (email?: string) => {
    setState(prev => ({ ...prev, loading: true, loginNotice: null }));

    try {
      await startCompanySignIn(email);
      // The browser navigates to the provider; keep the form loading until then.
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        loginNotice: {
          severity: "error",
          message: translate(
            error instanceof Error && error.message === COMPANY_SIGN_IN_UNAVAILABLE_ERROR
              ? "login.companySignInUnavailable"
              : "login.companySignInFailed"
          ),
        },
      }));
    }
  };

  const handleLoginSubmit = async (data: LoginFormData) => {
    setState(prev => ({ ...prev, loading: true, loginNotice: null }));

    try {
      const result = await authProvider?.login(data);
      await handleLoginResult(result, data.email, data.password);
    } catch (err) {
      if (err instanceof Error && err.message === COMPANY_SIGN_IN_REQUIRED_ERROR) {
        setState(prev => ({
          ...prev,
          loading: false,
          loginNotice: { severity: "warning", message: translate("login.companySignInRequired") },
        }));
        return;
      }
      handleError(err, translate("errors.loginFailed"));
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRegistrationSubmit = async (data: RegistrationFormData, candidate: RegistrationCandidate) => {
    setState(prev => ({ ...prev, loading: true }));

    let result: { jwtToken: string | null } | null;
    try {
      result =
        candidate.kind === UserType.CustomerService
          ? await dataProvider.registerUser({
              email: state.activeEmail,
              name: data.name,
              password: data.password,
              code: state.pendingValidationCode,
              kind: UserType.CustomerService,
            })
          : await dataProvider.registerUser({
              email: state.activeEmail,
              name: candidate.managerName,
              password: data.password,
              code: state.pendingValidationCode,
              kind: UserType.Manager,
              organizationName: candidate.organizationName,
            });
      if (!result) throw new Error("Registration failed");
    } catch (err) {
      if (candidate.kind === UserType.Manager && hasErrorCode(err, "ambiguous-customer-email")) {
        notify(translate("crm.accountManagers.dialog.ambiguousEmail"), { type: "error" });
      } else if (candidate.kind === UserType.Manager && hasErrorCode(err, "email-already-registered-error")) {
        notify(translate("registration.existingLoginConflict"), { type: "error" });
      } else {
        handleError(err, translate("errors.registrationFailed"));
      }
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    if (result.jwtToken) {
      // Store auth data using the centralized utility
      storeAuthData({
        token: result.jwtToken,
        status: LoginStatus.Registered,
        email: state.activeEmail,
        surface: "admin",
      });
      setState(prev => ({ ...prev, loading: false }));
      redirect("/");
      return;
    }

    try {
      const loginResult = await authProvider?.login({ email: state.activeEmail, password: data.password });
      await handleLoginResult(loginResult, state.activeEmail, data.password);
    } catch {
      clearMfaState();
      notify("registration.signInRequired", { type: "warning" });
    }
  };

  const handleForgotPassword = (emailArg?: string) => {
    setState(prev => ({
      ...prev,
      viewMode: ViewMode.ForgotPassword,
      activeEmail: emailArg || "",
    }));
  };

  const handleForgotPasswordSubmit = async (data: ForgotPasswordFormData) => {
    if (requiresCompanySignIn(data.email)) {
      setState(prev => ({
        ...prev,
        viewMode: ViewMode.Login,
        activeEmail: data.email,
        loginNotice: { severity: "warning", message: translate("login.companySignInRequired") },
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      await Promise.all([dataProvider.requestPasswordResetOTP(data.email), requestLearningPasswordReset(data.email)]);

      notify("forgotPassword.success", { type: "success" });
      setState(prev => ({
        ...prev,
        loading: false,
        viewMode: ViewMode.Login,
        activeEmail: data.email,
      }));
    } catch (err) {
      handleError(err, translate("forgotPassword.error"));
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleResetPasswordSubmit = async (data: ResetPasswordFormData) => {
    setState(prev => ({ ...prev, loading: true }));

    let result: { jwtToken: string | null };
    try {
      try {
        result = await dataProvider.resetPasswordWithValidationCode({
          code: state.pendingValidationCode,
          email: state.activeEmail,
          password: data.password,
        });
      } catch {
        await resetLearningPassword({
          code: state.pendingValidationCode,
          email: state.activeEmail,
          newPassword: data.password,
        });
        notify("resetPassword.success", { type: "success" });
        redirect(getAuthData()?.surface === "learning" ? "/learning" : "/");
        return;
      }
    } catch (err) {
      handleError(err, translate("resetPassword.error"));
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    if (result.jwtToken) {
      storeAuthData({
        token: result.jwtToken,
        status: LoginStatus.Registered,
        email: state.activeEmail,
        surface: "admin",
      });
      notify("resetPassword.success", { type: "success" });
      setState(prev => ({ ...prev, loading: false }));
      redirect("/");
      return;
    }

    try {
      const loginResult = await authProvider?.login({ email: state.activeEmail, password: data.password });
      if (!loginResult || typeof loginResult !== "object" || !("mfa" in loginResult) || !loginResult.mfa) {
        throw new Error("Login failed");
      }
      enterAdminMfa(loginResult.mfa as AdminMfaResponse);
      notify("resetPassword.success", { type: "success" });
    } catch {
      clearMfaState();
      notify("resetPassword.signInRequired", { type: "warning" });
    }
  };

  const handleAdminMfaSubmit = async (code: string, recovery: boolean) => {
    const challengeToken = state.mfaResponse?.challengeToken;
    if (!challengeToken) return;

    setState(prev => ({ ...prev, loading: true, mfaError: null }));
    try {
      const result = await completeAdminMfa({ challengeToken, code, recovery });
      if (result.status === AdminMfaStatus.InvalidCode) {
        setState(prev => ({
          ...prev,
          loading: false,
          mfaError: translate("mfa.invalidCode"),
          mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
        }));
        return;
      }

      if (result.status === AdminMfaStatus.Expired || result.status === AdminMfaStatus.Locked) {
        const lockedMessage =
          result.retryAfterSeconds === undefined || result.retryAfterSeconds === null
            ? translate("mfa.locked")
            : translate("mfa.lockedWithRetry", { seconds: result.retryAfterSeconds });
        setState(prev => ({
          ...prev,
          loading: false,
          mfaResponse: null,
          mfaError: result.status === AdminMfaStatus.Locked ? lockedMessage : translate("mfa.expired"),
          mfaIsReplacement: false,
          mfaAccessToken: null,
          mfaRecoveryCodes: [],
          mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
        }));
        return;
      }

      if (
        result.status === AdminMfaStatus.EnrollmentRequired ||
        result.status === AdminMfaStatus.VerificationRequired
      ) {
        setState(prev => ({
          ...prev,
          loading: false,
          mfaResponse: result,
          mfaError: null,
          mfaIsReplacement: recovery && result.status === AdminMfaStatus.EnrollmentRequired,
          mfaResetCodeVersion: prev.mfaResetCodeVersion + 1,
        }));
        return;
      }

      if (result.status !== AdminMfaStatus.Authenticated || !result.accessToken) {
        throw new Error("Admin MFA completion failed");
      }
      const accessToken = result.accessToken;
      const recoveryCodes = result.recoveryCodes;

      if (recoveryCodes?.length) {
        setState(prev => ({
          ...prev,
          loading: false,
          viewMode: ViewMode.AdminMfaRecoveryCodes,
          mfaResponse: null,
          mfaError: null,
          mfaAccessToken: accessToken,
          mfaRecoveryCodes: recoveryCodes,
        }));
        return;
      }

      await finishAdminMfaLogin(state.activeEmail, accessToken);
      redirect("/");
    } catch {
      setState(prev => ({ ...prev, loading: false, mfaError: translate("mfa.error") }));
    }
  };

  const handleRecoveryCodesSaved = async () => {
    if (!state.mfaAccessToken) return;

    setState(prev => ({ ...prev, loading: true, mfaError: null }));
    try {
      await finishAdminMfaLogin(state.activeEmail, state.mfaAccessToken);
      redirect("/");
    } catch {
      setState(prev => ({ ...prev, loading: false, mfaError: translate("mfa.error") }));
    }
  };

  const registrationCandidate = state.registrationCandidate;

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: "100vh",
        backgroundColor: color.background2,
        position: "relative",
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          position: "absolute",
          top: `${space.xxl1}px`,
          left: `${space.xxl1}px`,
        }}
      >
        <Logo />
      </Box>

      {/* Centered Login Form */}
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card>
          <CardContent sx={{ padding: `${space.xxl1}px` }}>
            {state.viewMode === ViewMode.Registration && registrationCandidate && (
              <RegistrationForm
                loading={state.loading}
                candidate={registrationCandidate}
                onSubmit={data => handleRegistrationSubmit(data, registrationCandidate)}
              />
            )}
            {state.viewMode === ViewMode.Login && (
              <>
                {state.loginNotice && (
                  <Alert severity={state.loginNotice.severity} role="alert" sx={{ marginBottom: `${space.sm}px` }}>
                    {state.loginNotice.message}
                  </Alert>
                )}
                <LoginForm
                  defaultEmail={state.activeEmail}
                  loading={state.loading}
                  onSubmit={handleLoginSubmit}
                  onCompanySignIn={handleCompanySignIn}
                  onForgotPassword={handleForgotPassword}
                />
              </>
            )}
            {state.viewMode === ViewMode.ForgotPassword && (
              <ForgotPasswordForm
                loading={state.loading}
                defaultEmail={state.activeEmail}
                onSubmit={handleForgotPasswordSubmit}
                onBackToLogin={() => setState(prev => ({ ...prev, viewMode: ViewMode.Login }))}
              />
            )}
            {state.viewMode === ViewMode.ResetPassword && (
              <ResetPasswordForm
                loading={state.loading}
                onSubmit={handleResetPasswordSubmit}
                onBackToLogin={() => setState(prev => ({ ...prev, viewMode: ViewMode.Login }))}
              />
            )}
            {state.viewMode === ViewMode.AdminMfa && (
              <AdminMfaForm
                response={state.mfaResponse}
                loading={state.loading}
                error={state.mfaError}
                isReplacement={state.mfaIsReplacement}
                resetCodeVersion={state.mfaResetCodeVersion}
                onSubmit={handleAdminMfaSubmit}
                onBack={() => clearMfaState()}
              />
            )}
            {state.viewMode === ViewMode.AdminMfaRecoveryCodes && (
              <>
                <h1>{translate("mfa.recoveryCodesTitle")}</h1>
                <Alert severity="warning" role="alert">
                  {translate("mfa.recoveryCodesWarning")}
                </Alert>
                {state.mfaError && (
                  <Alert severity="error" role="alert" sx={{ marginTop: `${space.sm}px` }}>
                    {state.mfaError}
                  </Alert>
                )}
                <Box component="ul" data-testid="admin-mfa-recovery-codes">
                  {state.mfaRecoveryCodes.map(code => (
                    <li key={code}>{code}</li>
                  ))}
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleRecoveryCodesSaved}
                  disabled={state.loading || !state.mfaAccessToken}
                >
                  {state.loading ? translate("mfa.verifying") : translate("mfa.codesSaved")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Container>
  );
};
